/* eslint-disable radix */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import _debounce from 'lodash/debounce';
import PropTypes from 'prop-types';

import updateAllPadding from '../updateAllPadding';
import Button from '../../../../componentLibrary/Button';
import Input from '../../../../componentLibrary/Input';
import HorizontalRule from '../../../../componentLibrary/HorizontalRule';
import FontSelect from '../../../../componentLibrary/FontSelect';
import ElementPadding from '../../../../componentLibrary/ElementPadding';
import paddingChange from '../../paddingChange';
import InputValidator from '../../../../componentLibrary/InputValidator';
import imageSources from '../../../../constants/imageSources/imageSources';
import * as columnItemActions from '../../../../actions/columnItem';
import { getLineHeight, getLineHeightMultiplier } from '../../../CenterPanel/LineHeight';
import rules from '../../../../constants/businessRules/businessRules';
import * as Styled from './styles';
import { sessionType } from '../../../../types';

const ValidatedLink = InputValidator(Input, { url: 1, required: true });
const ValidatedSizing = InputValidator(Input, { min: 0, max: 50 });
const ValidatedRadius = InputValidator(Input, { min: 0, max: 20 });

const ButtonSetting = ({ selectedElementData, session, dispatch }) => {
    const [displayButtonBodyColorPicker, setDisplayButtonBodyColorPicker] = useState(false);
    const [displayButtonTextColorPicker, setDisplayButtonTextColorPicker] = useState(false);
    const [buttonTextInput, setButtonTextInput] = useState(selectedElementData.content.text);
    const [buttonLinkInput, setButtonLinkInput] = useState(selectedElementData.content.href);
    const [selectedElmIdx, setSelectedElmIdx] = useState('');

    const updateContent = (key, value) => {
        const { blockIndex, columnIdx, elmIdx, content } = selectedElementData;
        let newContentValues;
        if (key === 'updateAllPadding') {
            newContentValues = updateAllPadding(content, value);
        } else if (key === 'fontSize') {
            const { fontSize, lineHeight } = content;
            const lineHeightMultiplier = getLineHeightMultiplier(fontSize, lineHeight);

            // we need to make sure the line height is updated using the multiplier so we keep the same proportion
            newContentValues = { ...content, [key]: value, lineHeight: getLineHeight(value, lineHeightMultiplier) };
        } else if (key === 'fontFamily') {
            const { fontID, value: fontValue } = value;
            newContentValues = { ...content, [key]: fontValue.trim(), fontID };
        } else {
            newContentValues = { ...content, [key]: value };
        }
        const newContent = { ...content, ...newContentValues };
        dispatch(columnItemActions.updateColumnItemContent({ blockIndex, columnIdx, elmIdx, newContent }));
    };

    const debouncedUpdateContent = _debounce(updateContent, 250);

    // we are not putting debouncedUpdateContent into the deps array because its making this an infinite loop
    useEffect(() => {
        debouncedUpdateContent.cancel();
        debouncedUpdateContent('text', buttonTextInput);
    }, [buttonTextInput]); // eslint-disable-line

    // we are not putting debouncedUpdateContent into the deps array because its making this an infinite loop
    useEffect(() => {
        debouncedUpdateContent.cancel();
        debouncedUpdateContent('href', buttonLinkInput);
    }, [buttonLinkInput]); // eslint-disable-line

    // once a new element is selected, we should update its href into the the button settings
    useEffect(() => {
        if (selectedElementData.elmIdx !== selectedElmIdx) {
            setSelectedElmIdx(selectedElementData.elmIdx);
            setButtonTextInput(selectedElementData.content.text);
            setButtonLinkInput(selectedElementData.content.href);
        }
    }, [
        selectedElmIdx,
        selectedElementData.elmIdx,
        selectedElementData.content.text,
        selectedElementData.content.href,
    ]);

    const { content } = selectedElementData;
    const { user, integration } = session;
    const {
        customization: { colors },
        features,
        isAdminOrParentAdmin,
    } = user;
    const brandColors = JSON.parse(colors).filter((item) => Object.prototype.hasOwnProperty.call(item, 'color'));
    const hasFullStylingAccess =
        features.EMAIL_DESIGNER_STYLING_ACCESS === undefined ? true : features.EMAIL_DESIGNER_STYLING_ACCESS;
    const hasCustomizationFeature = features.EMAIL_DESIGNER_CUSTOMIZATION;
    const hasCustomizationAccess = isAdminOrParentAdmin && (integration === 'LOCALHOST' || integration === 'APP');

    const {
        align,
        fontFamily,
        fontID,
        fontSize,
        lineHeight,
        fontStyle,
        fontWeight,
        textDecoration,
        color,
        backgroundColor,
        borderRadius,
        innerHorizontalPadding,
        innerVerticalPadding,
        paddingTop,
        paddingRight,
        paddingBottom,
        paddingLeft,
    } = content;
    const currLineHeight = String(parseFloat(lineHeight.split('px')[0] / fontSize.split('px')[0]).toFixed(1)).replace(
        /.0/g,
        ''
    );

    return (
        <div>
            <Styled.Row>
                <HorizontalRule />
                <ElementPadding
                    paddingTop={paddingTop}
                    paddingBottom={paddingBottom}
                    paddingLeft={paddingLeft}
                    paddingRight={paddingRight}
                    elementType="button"
                    onChange={(event) => {
                        const payload = paddingChange({ event, paddingBottom, paddingLeft, paddingRight, paddingTop });
                        if (!payload) {
                            return;
                        }
                        const { key, value = 0 } = payload;
                        updateContent(key, value);
                    }}
                />
                <HorizontalRule />
                <ValidatedLink
                    type="text"
                    width={180}
                    placeholder="Enter link URL here"
                    value={buttonLinkInput}
                    label="BUTTON LINK"
                    icon="link"
                    onChange={(event) => setButtonLinkInput(event.target.value)}
                />
                <Input
                    type="text"
                    width={180}
                    value={buttonTextInput}
                    label="BUTTON TEXT"
                    id="button-setting-text"
                    customIcon={imageSources.ICONS.BUTTON_TEXT}
                    onChange={(event) => setButtonTextInput(event.target.value)}
                />
                <Styled.Label>Font Style</Styled.Label>
                <Styled.SmallLabel>Font</Styled.SmallLabel>
                <FontSelect
                    width={180}
                    marginBottom={2}
                    type="fontFamily"
                    label="Font Family"
                    value={fontFamily}
                    fontID={fontID}
                    onChange={(selectedOption) => {
                        const selectedFontFamilyValue = selectedOption.value.trim();
                        if (selectedFontFamilyValue === fontFamily) {
                            return;
                        }
                        updateContent('fontFamily', selectedOption);
                    }}
                />
                <Styled.InputWrapper>
                    <div>
                        <Styled.SmallLabel>Size</Styled.SmallLabel>
                        <FontSelect
                            width={82}
                            marginBottom={6}
                            type="fontSize"
                            label="Font Size"
                            value={fontSize}
                            icon="format_size"
                            onChange={(selectedOption) => {
                                const selectedFontSize = selectedOption.value.trim();
                                if (selectedFontSize === fontSize) {
                                    return;
                                }
                                updateContent('fontSize', selectedFontSize);
                            }}
                        />
                    </div>
                    <div>
                        <Styled.SmallLabel>Line height</Styled.SmallLabel>
                        <FontSelect
                            width={82}
                            marginBottom={6}
                            type="lineHeight"
                            label="Line Height"
                            value={currLineHeight}
                            icon="format_line_spacing"
                            onChange={(selectedOption) => {
                                const selectedLineHeight = selectedOption.value.trim();
                                if (selectedLineHeight === currLineHeight) {
                                    return;
                                }
                                const newLineHeight = `${parseInt(
                                    parseFloat(selectedLineHeight) * parseInt(fontSize.split('px')[0])
                                )}px`;
                                updateContent('lineHeight', newLineHeight);
                            }}
                        />
                    </div>
                </Styled.InputWrapper>
                <Styled.SmallLabel>Color</Styled.SmallLabel>
                <Button
                    type="colorPicker"
                    width={180}
                    label="Text"
                    icon="format_color_fill"
                    shouldDisplayColorPicker={displayButtonTextColorPicker}
                    color={color}
                    presetColors={brandColors}
                    hasFullStylingAccess={hasFullStylingAccess}
                    hasCustomizationFeature={hasCustomizationFeature}
                    hasCustomizationAccess={hasCustomizationAccess}
                    value={color}
                    onClick={() => setDisplayButtonTextColorPicker(true)}
                    onChangeComplete={(pickedColor) => {
                        const { hex } = pickedColor;
                        updateContent('color', hex);
                    }}
                    onClose={() => setDisplayButtonTextColorPicker(false)}
                    onChange={(event) => {
                        const { value } = event.target;
                        if (value.length > 20) {
                            return;
                        }
                        if (value[0] === '#' && value.length > 7) {
                            return;
                        }
                        updateContent('color', value);
                    }}
                />
                <div style={{ width: '100%', height: 32 }}>
                    <Button
                        type="simple"
                        width={32}
                        label="Bold"
                        ariaLabel="Apply bold to button's text"
                        icon="bold"
                        iconSize="14"
                        color={fontWeight === 'bold' ? '#0084d1' : '#727273'}
                        onClick={() => {
                            const value = fontWeight === 'bold' ? 'normal' : 'bold';
                            updateContent('fontWeight', value);
                        }}
                    />
                    <Button
                        type="simple"
                        width={32}
                        label="Italic"
                        ariaLabel="Apply italic to button's text"
                        icon="italic"
                        iconSize={14}
                        color={fontStyle === 'italic' ? '#0084d1' : '#727273'}
                        onClick={() => {
                            const value = fontStyle === 'italic' ? 'normal' : 'italic';
                            updateContent('fontStyle', value);
                        }}
                    />
                    <Button
                        type="simple"
                        width={32}
                        label="ULine"
                        icon="underline"
                        ariaLabel="Apply underline to button's text"
                        iconSize={14}
                        color={textDecoration === 'underline' ? '#0084d1' : '#727273'}
                        onClick={() => {
                            const value = textDecoration === 'underline' ? 'none' : 'underline';
                            updateContent('textDecoration', value);
                        }}
                    />
                </div>
                <Styled.Label>Button Style</Styled.Label>
                <Styled.InputWrapper>
                    <div>
                        <Styled.SmallLabel>Width</Styled.SmallLabel>
                        <ValidatedSizing
                            type="number-small"
                            width={85}
                            initialValue={innerHorizontalPadding.toString()}
                            label="Hor. Spacing"
                            icon="settings_ethernet"
                            onChange={(event) => {
                                const value = +event.target.value;
                                if (value === innerHorizontalPadding) {
                                    return;
                                }
                                updateContent('innerHorizontalPadding', value);
                            }}
                        />
                    </div>
                    <div>
                        <Styled.SmallLabel>Height</Styled.SmallLabel>
                        <ValidatedSizing
                            type="number-small"
                            width={85}
                            initialValue={innerVerticalPadding.toString()}
                            label="Ver. Spacing"
                            icon="settings_ethernet"
                            rotate
                            onChange={(event) => {
                                const value = +event.target.value;
                                if (value === innerVerticalPadding) {
                                    return;
                                }
                                updateContent('innerVerticalPadding', value);
                            }}
                        />
                    </div>
                </Styled.InputWrapper>
                <Styled.InputWrapper>
                    <div style={{ height: 54 }}>
                        <Styled.SmallLabel>Corners</Styled.SmallLabel>
                        <ValidatedRadius
                            type="number-small"
                            width={85}
                            initialValue={borderRadius.toString()}
                            label="Radius"
                            icon="rounded_corner"
                            min="0"
                            max={rules.MAXIMUM_ALLOWABLE_BUTTON_BORDER_RADIUS}
                            onChange={(event) => {
                                const value = +event.target.value;
                                if (value === borderRadius) {
                                    return;
                                }
                                updateContent('borderRadius', value);
                            }}
                        />
                    </div>
                    <div>
                        <Styled.SmallLabel margin="0 0 0 6px">Alignment</Styled.SmallLabel>
                        <Styled.AlignWrapper>
                            <Button
                                type="simple"
                                icon="align-left"
                                width={28}
                                marginBottom={6}
                                iconSize={15}
                                color="#727272"
                                label="Align Left"
                                ariaLabel="Align button to left side"
                                iconPadding={7}
                                onClick={() => {
                                    if (align === 'left') {
                                        return;
                                    }
                                    updateContent('align', 'left');
                                }}
                            />
                            <Button
                                type="simple"
                                icon="align-center"
                                width={28}
                                marginBottom={6}
                                iconSize={15}
                                iconPadding={7}
                                color="#727272"
                                label="Align Center"
                                ariaLabel="Align button to center"
                                onClick={() => {
                                    if (align === 'center') {
                                        return;
                                    }
                                    updateContent('align', 'center');
                                }}
                            />
                            <Button
                                type="simple"
                                icon="align-right"
                                width={28}
                                marginBottom={6}
                                iconSize={15}
                                iconPadding={7}
                                color="#727272"
                                label="Align Right"
                                ariaLabel="Align button to right side"
                                onClick={() => {
                                    if (align === 'right') {
                                        return;
                                    }
                                    updateContent('align', 'right');
                                }}
                            />
                        </Styled.AlignWrapper>
                    </div>
                </Styled.InputWrapper>
                <Styled.SmallLabel>Color fill</Styled.SmallLabel>
                <Button
                    type="colorPicker"
                    width={180}
                    label="Body"
                    icon="format_color_fill"
                    shouldDisplayColorPicker={displayButtonBodyColorPicker}
                    presetColors={brandColors}
                    hasFullStylingAccess={hasFullStylingAccess}
                    hasCustomizationFeature={hasCustomizationFeature}
                    hasCustomizationAccess={hasCustomizationAccess}
                    color={backgroundColor}
                    value={backgroundColor}
                    screenCheck
                    onClick={() => setDisplayButtonBodyColorPicker(true)}
                    onChangeComplete={(incomingColor) => {
                        const { hex } = incomingColor;
                        updateContent('backgroundColor', hex);
                    }}
                    onClose={() => setDisplayButtonBodyColorPicker(false)}
                    onChange={(event) => {
                        const { value } = event.target;
                        if (value.length > 20) {
                            return;
                        }
                        if (value[0] === '#' && value.length > 7) {
                            return;
                        }
                        updateContent('backgroundColor', value);
                    }}
                />
            </Styled.Row>
        </div>
    );
};
const mapStateToProps = (state) => ({
    session: state.session,
});

ButtonSetting.propTypes = {
    selectedElementData: PropTypes.object.isRequired,
    session: sessionType.isRequired,
    dispatch: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(ButtonSetting);
